import React, {useContext, useState} from 'react';
import PropTypes from 'prop-types';
import AppBar from '@material-ui/core/AppBar';
import CssBaseline from '@material-ui/core/CssBaseline';
import Drawer from '@material-ui/core/Drawer';
import Fab from '@material-ui/core/Fab';
import Hidden from '@material-ui/core/Hidden';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import MenuIcon from '@material-ui/icons/Menu';
import HomeIcon from '@material-ui/icons/Home';
import History from '@material-ui/icons/History';
import TrackChanges from '@material-ui/icons/TrackChanges';
import Book from '@material-ui/icons/Book';
import AddCircle from '@material-ui/icons/Add';
import Search from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import TextField from '@material-ui/core/TextField';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useHistory, useLocation } from 'react-router-dom';
import {PermissionsContext} from '../utils/constants';
import CheckIcon from '@material-ui/icons/Check';
import ViewComfyIcon from '@material-ui/icons/ViewComfy';

import Constants from '../utils/constants';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    width: '100%'
  },
  drawer: {
    [theme.breakpoints.up('sm')]: {
      width: '200px',
      flexShrink: 0,
    },
  },
  appBar: {
      width: `100%`,
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  toolbar: theme.mixins.toolbar,
  drawerPaper: {
    width: '240px',
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  dashboardLink: {
    padding: '10px 24px',
  },
  linkActive: {
    padding: '10px 24px',
    borderBottom: '3px solid white'
  },
  padding: {
    paddingLeft: '1rem'
  },
  transaltaIconContainer: {
    display: 'flex', 
    justifyContent: 'center'
  },
  transaltaIcon: {
    padding: '5px',
    width: '64px'
  },
  circleSpan: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexGrow: 1,
    paddingRight: '5%',
    top: '2rem',
    position: 'relative'
  },
  searchBar: {
    display: 'none',
    justifyContent: 'space-between',
    position: 'absolute',
    left: 0,
    right: 0,
    zIndex: '1200',
    backgroundColor: 'white',
    height: '6.5rem'
  },
  searchInput: {
    width: '80%',
    margin: '1.5rem',
    marginLeft: '2rem'
  },
  closeIcon: {
    display: 'flex',
    alignItems: 'center',
    margin: '1rem',
  },
  searchIconDiv: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexGrow: 1,
    alignItems: 'center',
  },
  cursorPointer: {
    cursor: 'pointer'
  }
}));

export default function DashboardHeader({ menus, showCreateInitiative = true }) {
  const { container } = useState();
  const classes = useStyles();
  const theme = useTheme();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const routeLocation = useLocation();
  const history = useHistory();
  const { isAdmin, isApprover, isReviewer } = useContext(PermissionsContext);

  const clickedFolder = (folder) => () => {
    if (folder === Constants.nullPath) {
      return;
    }
    history.push(folder);
  };

  const clickedCreateInitiative = () => {
    history.push('/ui/createinitiative');
  };

  const getClasses = (location) => {
    if (location === routeLocation.pathname) {
      return classes.linkActive;
    }

    return classes.dashboardLink;
  };

  const handleDrawerToggle = () => {
    setMenuOpen(!menuOpen);
  };

  const gotToHref = (href) => () => {
    history.push(href)
  };

  const makeDrawer = (name, Icon, href) => {
    return (
    <ListItem button key={name} onClick={gotToHref(href)}>
      <Icon/> <span className={classes.padding}/>{name}
    </ListItem>
    );
  };

  const getCreateInitiativeIcon = () => {
    if (!showCreateInitiative) {
      return '';
    }
    return (
      <span className={classes.circleSpan}>
        <Fab color="secondary" aria-label="add" onClick={clickedCreateInitiative}>
          <AddCircle/>
        </Fab>
      </span>
    );
  };

  const openSearch = () => {
    document.getElementById('search-box').style.display = 'flex';
    document.getElementById('outlined-search').focus();
  };

  const closeSearch = () => {
    document.getElementById('search-box').style.display = 'none';
  };

  const searchInputChanged = (event) => {
    if(event.keyCode === 13) {
      const searchTerm = event.target.value;
      history.push(`/ui/search/${searchTerm}`);
    }
  };

  const drawer = () => {
    return (
      <nav>
        <div className={classes.transaltaIconContainer}>
          <img src={process.env.PUBLIC_URL + '/SimpleBlend_CorpBlue.png'}
               alt="TransAlta" id="logo"
               className={classes.transaltaIcon} />
        </div>
        <List>
          {makeDrawer('Current Projects', HomeIcon, '/')}
          {makeDrawer('Archive', History, '/ui/past/initiatives')}
          {isAdmin || isApprover ? makeDrawer('Change Tracker', TrackChanges, '/ui/changetracker-view') : ''}
          {isApprover? makeDrawer('Approver View', CheckIcon, '/ui/approver-view') : ''}
          {isReviewer? makeDrawer('Reviewer View', ViewComfyIcon, '/ui/reviewer-view') : ''}
          {makeDrawer('Knowledge Base', Book, '/ui/knowledge-base')}
          {isAdmin? makeDrawer('Admin', History, '/ui/admin/reviewers') : ''}
        </List>

      </nav>
    )};

  const getSearchDisplay = () => {
    return (
      <div className={classes.searchBar} id={'search-box'}>
        <TextField id="outlined-search"
                   placeholder="Search"
                   type="search"
                   onKeyDown={searchInputChanged}
                   className={classes.searchInput}
                   variant="outlined" />
        <span className={classes.closeIcon} onClick={closeSearch}>
          <CloseIcon className={classes.cursorPointer}/>
        </span>
      </div>
    )
  };

    return (
      <div className={classes.root}>
        <CssBaseline />
        {getSearchDisplay()}
        <AppBar position="fixed" className={classes.appBar}>
          <Toolbar >
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              className={classes.menuButton}
            >
              <MenuIcon />
            </IconButton>
            { menus.map((menu, index) => {
              return (
                <Typography variant="h6" noWrap
                            onClick={clickedFolder(menu.path)}
                            key={'header-menu-' + index}
                            className={[getClasses(menu.path), classes.cursorPointer].join(' ')}>
                {menu.name}
              </Typography>
              );
            })}
            <div className={classes.searchIconDiv}>
            {getCreateInitiativeIcon()}

              <Search onClick={openSearch} className={classes.cursorPointer}/>
            </div>
          </Toolbar>
        </AppBar>
        nav className={classes.drawer} aria-label="mailbox folders">
        {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
        <Hidden implementation="css">
          <Drawer
            container={container}
            variant="temporary"
            anchor={theme.direction === 'rtl' ? 'right' : 'left'}
            open={menuOpen}
            onClose={handleDrawerToggle}
            classes={{
              paper: classes.drawerPaper,
            }}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
          >
            {drawer()}
          </Drawer>
        </Hidden>
      </div>
    );
}

DashboardHeader.propTypes = {
  menus: PropTypes.array.isRequired,
  showCreateInitiative: PropTypes.bool
};
